import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { X, Calendar, Clock, User, Phone, Stethoscope } from 'lucide-react';
import { patientsApi } from '../services/api';
import { authService } from '../services/authApi';
import { Patient, Appointment, CalendarEvent } from '../types';
import { format, isValid } from 'date-fns';
import { es } from 'date-fns/locale';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Appointment>) => void;
  onDelete?: () => void;
  appointment?: CalendarEvent | null;
  selectedDate?: Date | null;
  isLoading?: boolean;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  appointment,
  selectedDate,
  isLoading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showPatientList, setShowPatientList] = useState(false);
  
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<Partial<Appointment>>();

  // Fetch patients for search
  const { data: patientsResponse } = useQuery(
    ['patients-search', searchTerm],
    () => patientsApi.getAll({ search: searchTerm, limit: 10 }),
    {
      enabled: showPatientList && searchTerm.length > 2,
    }
  );

  // Fetch dentists for dropdown
  const { data: dentistsResponse } = useQuery(
    ['dentists'],
    () => authService.getDentists(),
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  const patients = patientsResponse?.data?.patients || [];
  const dentists = dentistsResponse?.dentists || [];

  useEffect(() => {
    if (appointment) {
      const startDate = appointment.start ? new Date(appointment.start) : null;
      const endDate = appointment.end ? new Date(appointment.end) : null;

      // Precargar el nombre del paciente en el campo de búsqueda
      if (appointment.patient && typeof appointment.patient === 'object') {
        const patientName = `${appointment.patient.firstName || ''} ${appointment.patient.lastName || ''}`.trim();
        setSearchTerm(patientName);
      }

      reset({
        title: appointment.title || '',
        description: appointment.description || '',
        startTime: startDate && isValid(startDate) ? format(startDate, "yyyy-MM-dd'T'HH:mm") : '',
        endTime: endDate && isValid(endDate) ? format(endDate, "yyyy-MM-dd'T'HH:mm") : '',
        status: appointment.status as any || 'scheduled',
        type: appointment.type as any || 'consultation',
        dentist: appointment.dentist || '',
        room: appointment.room || '',
        notes: appointment.notes || '',
        patient: appointment.patient?._id || appointment.patient || '',
      });
    } else if (selectedDate) {
      // Limpiar el campo de búsqueda para nuevas citas
      setSearchTerm('');
      
      const selDate = selectedDate instanceof Date ? selectedDate : new Date(selectedDate as any);
      const defaultStart = selDate && isValid(selDate) ? format(selDate, "yyyy-MM-dd'T'HH:mm") : '';
      const defaultEnd = selDate && isValid(selDate) ? format(new Date(selDate.getTime() + 60 * 60 * 1000), "yyyy-MM-dd'T'HH:mm") : '';

      reset({
        title: '',
        description: '',
        startTime: defaultStart,
        endTime: defaultEnd,
        status: 'scheduled',
        type: 'consultation',
        dentist: '',
        room: '',
        notes: '',
        patient: '',
      });
    } else {
      // Reset completo para nueva cita sin fecha seleccionada
      setSearchTerm('');
      reset({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        status: 'scheduled',
        type: 'consultation',
        dentist: '',
        room: '',
        notes: '',
        patient: '',
      });
    }
  }, [appointment, selectedDate, reset]);

  // Efecto separado para manejar la búsqueda del paciente por ID cuando estamos editando
  useEffect(() => {
    if (appointment && appointment.patient && typeof appointment.patient === 'string' && patients.length > 0) {
      const patientId = appointment.patient as string;
      const patient = patients.find((p: Patient) => p._id === patientId);
      if (patient) {
        const patientName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
        setSearchTerm(prev => prev !== patientName ? patientName : prev);
        setValue('patient', patientId);
      }
    }
  }, [appointment?.patient, patients, setValue]);

  const onSubmit = (data: Partial<Appointment>) => {
    onSave(data);
  };

  const handlePatientSelect = (patient: Patient) => {
    setValue('patient', patient._id);
    setSearchTerm(`${patient.firstName} ${patient.lastName}`);
    setShowPatientList(false);
  };

  // Cerrar lista de búsqueda al hacer click fuera
  const handleInputBlur = (e: React.FocusEvent) => {
    // Delay para permitir el click en los elementos de la lista
    setTimeout(() => {
      setShowPatientList(false);
    }, 200);
  };

  const selectedPatientId = watch('patient');
  const selectedPatient = patients.find((p: Patient) => p._id === selectedPatientId);

  // Si estamos editando una cita, el paciente seleccionado puede venir de appointment
  const currentPatient = selectedPatient || (appointment?.patient && typeof appointment.patient === 'object' ? {
    _id: appointment.patient._id,
    firstName: appointment.patient.firstName,
    lastName: appointment.patient.lastName,
    email: appointment.patient.email || '',
    phone: appointment.patient.phone || ''
  } : null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto desktop-scalable">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full max-w-lg md:max-w-2xl lg:max-w-3xl mx-auto desktop-card">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 lg:desktop-padding">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 lg:desktop-text-lg">
                  {appointment ? 'Editar Cita' : 'Nueva Cita'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Patient Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 lg:desktop-text-sm">
                    Paciente *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowPatientList(true);
                      }}
                      onFocus={() => setShowPatientList(true)}
                      onBlur={handleInputBlur}
                      placeholder="Buscar paciente..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500 lg:desktop-text-base"
                    />
                    {showPatientList && patients.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {patients.map((patient: Patient) => (
                          <div
                          key={patient._id}
                          onClick={() => handlePatientSelect(patient)}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                          <div className="font-medium">{patient.firstName} {patient.lastName}</div>
                          <div className="text-sm text-gray-500">{patient.email} • {patient.phone}</div>
                          </div>
                        ))}
                        </div>
                    )}
                  </div>
                  {currentPatient && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium">{currentPatient.firstName} {currentPatient.lastName}</span>
                      </div>
                      <div className="flex items-center mt-1">
                        <Phone className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">{currentPatient.phone}</span>
                      </div>
                    </div>
                  )}
                  {errors.patient && (
                    <p className="mt-1 text-sm text-red-600">Selecciona un paciente</p>
                  )}
                  {/* Campo oculto para el ID del paciente */}
                  <input 
                    type="hidden" 
                    {...register('patient', { required: 'Selecciona un paciente' })}
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 lg:desktop-text-sm">
                    Título *
                  </label>
                  <input
                    type="text"
                    {...register('title', { required: 'El título es requerido' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500 lg:desktop-text-base"
                    placeholder="Ej: Limpieza dental, Consulta, etc."
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha y Hora Inicio *
                    </label>
                    <input
                      type="datetime-local"
                      {...register('startTime', { required: 'La fecha de inicio es requerida' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    />
                    {errors.startTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.startTime.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha y Hora Fin *
                    </label>
                    <input
                      type="datetime-local"
                      {...register('endTime', { required: 'La fecha de fin es requerida' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    />
                    {errors.endTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.endTime.message}</p>
                    )}
                  </div>
                </div>

                {/* Type and Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo *
                    </label>
                    <select
                      {...register('type', { required: 'El tipo es requerido' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    >
                      <option value="consultation">Consulta</option>
                      <option value="cleaning">Limpieza</option>
                      <option value="treatment">Tratamiento</option>
                      <option value="follow-up">Seguimiento</option>
                      <option value="emergency">Emergencia</option>
                    </select>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado
                    </label>
                    <select
                      {...register('status')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    >
                      <option value="scheduled">Programada</option>
                      <option value="confirmed">Confirmada</option>
                      <option value="completed">Completada</option>
                      <option value="cancelled">Cancelada</option>
                      <option value="no-show">No se presentó</option>
                    </select>
                  </div>
                </div>

                {/* Dentist and Room */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dentista *
                    </label>
                    <select
                      {...register('dentist', { required: 'El dentista es requerido' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500 bg-white"
                    >
                      <option value="">Selecciona un dentista</option>
                      {dentists.map((dentist) => (
                        <option key={dentist.id} value={dentist.name}>
                          {dentist.name}
                        </option>
                      ))}
                    </select>
                    {errors.dentist && (
                      <p className="mt-1 text-sm text-red-600">{errors.dentist.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Consultorio
                    </label>
                    <input
                      type="text"
                      {...register('room')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                      placeholder="Consultorio 1"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    placeholder="Descripción de la cita..."
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas
                  </label>
                  <textarea
                    {...register('notes')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    placeholder="Notas adicionales..."
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-dental-600 text-base font-medium text-white hover:bg-dental-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="spinner mr-2"></div>
                ) : null}
                {appointment ? 'Actualizar' : 'Crear'} Cita
              </button>
              
              {onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Eliminar
                </button>
              )}
              
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppointmentModal;















